import { z } from 'zod';

import { mcqOptionPublicSchema, questionDifficultySchema, questionTypeSchema } from './question';

// BR-2: assessment tipleri yalnızca baseline | ai_dependency.
export const assessmentTypeSchema = z.enum(['baseline', 'ai_dependency']);
export type AssessmentType = z.infer<typeof assessmentTypeSchema>;

export const assessmentStatusSchema = z.enum(['in_progress', 'completed']);
export type AssessmentStatus = z.infer<typeof assessmentStatusSchema>;

/**
 * A single item a client used to seed into a new assessment.
 *
 * PRE-100: the server picks the questions now, and this is IGNORED. It is still
 * accepted because the build on somebody's phone sends it and does not update
 * when the API does; rejecting it would break every installed app. PRE-107 stops
 * mobile sending it, and the field goes once those builds have rolled over.
 *
 * @deprecated ignored by the server since PRE-100.
 */
export const assessmentItemInputSchema = z.object({
  questionId: z.string().min(1).optional(),
  prompt: z.string().min(1),
});
export type AssessmentItemInput = z.infer<typeof assessmentItemInputSchema>;

/** POST /v1/assessments body. */
export const createAssessmentInputSchema = z.object({
  type: assessmentTypeSchema,
  // BR-4: position array index'i ile sıralanır. `items` PRE-100'den beri sunucu
  // tarafından yok sayılıyor (yukarıdaki nota bak).
  items: z.array(assessmentItemInputSchema).max(50).optional(),
});
export type CreateAssessmentInput = z.infer<typeof createAssessmentInputSchema>;

/** POST /v1/assessments/:id/items/:itemId/answer body. */
/**
 * POST /v1/assessments/:id/items/:itemId/answer body.
 * Carries the FR-4 behavioural signals collected per item.
 */
export const answerAssessmentItemInputSchema = z.object({
  answer: z.string().min(1),
  /** Time spent on the item, in milliseconds. */
  durationMs: z.number().int().min(0).optional(),
  /** Number of hints requested for this item (hint ladder level reached). */
  hintCount: z.number().int().min(0).optional(),
  /** Self-reported confidence, 1-5. */
  confidence: z.number().int().min(1).max(5).optional(),
});
export type AnswerAssessmentItemInput = z.infer<typeof answerAssessmentItemInputSchema>;

export const assessmentItemSchema = z.object({
  id: z.string(),
  questionId: z.string().nullable(),
  position: z.number().int(),
  prompt: z.string(),
  /** PRE-107: public bank fields so the client can render without GET /questions. */
  type: questionTypeSchema,
  difficulty: questionDifficultySchema,
  skillId: z.string(),
  hints: z.array(z.string()),
  expectedConcepts: z.array(z.string()),
  /** Present for `mcq`; null otherwise. BR-59: never includes `correct`. */
  options: z.array(mcqOptionPublicSchema).nullable(),
  answer: z.string().nullable(),
  answeredAt: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  hintCount: z.number().int(),
  confidence: z.number().int().nullable(),
});
export type AssessmentItemResponse = z.infer<typeof assessmentItemSchema>;

export const assessmentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  type: assessmentTypeSchema,
  status: assessmentStatusSchema,
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  items: z.array(assessmentItemSchema),
});
export type AssessmentResponse = z.infer<typeof assessmentSchema>;
